/* ──────────────────────────────────────────────────────────────────────────
 * Incident state machine — the §21 workflow spine.
 *
 *   detected → triaged → authorised → dispatched → acknowledged → resolved
 *
 * Why a machine rather than a status string: every stage boundary is where a
 * government system either earns or loses its evidence. "Who authorised
 * this, on what basis, and how long did the agency take to acknowledge"
 * are only answerable if the transitions are constrained and recorded. A
 * free-text status column lets an incident jump from detected to resolved
 * with nothing in between, and then nobody can reconstruct what happened.
 *
 * The transitions here are deliberately strict:
 *   - Nothing reaches `dispatched` without passing through `authorised`,
 *     because dispatching agencies is a consequential government action and
 *     §2 requires a human in that loop.
 *   - `cancelled` is reachable from anywhere before resolution, because
 *     false positives are normal and must be closeable without pretending
 *     they were resolved.
 *   - Nothing leaves `resolved`. A reopened incident is a NEW incident that
 *     references the old one — otherwise resolution time becomes unmeasurable.
 * ────────────────────────────────────────────────────────────────────────── */

export const STATUS = {
  DETECTED: "detected",
  TRIAGED: "triaged",
  AUTHORISED: "authorised",
  DISPATCHED: "dispatched",
  ACKNOWLEDGED: "acknowledged",
  RESOLVED: "resolved",
  CANCELLED: "cancelled",
};

/* Allowed next states. Anything not listed is rejected. */
export const TRANSITIONS = {
  [STATUS.DETECTED]:     [STATUS.TRIAGED, STATUS.CANCELLED],
  [STATUS.TRIAGED]:      [STATUS.AUTHORISED, STATUS.CANCELLED],
  [STATUS.AUTHORISED]:   [STATUS.DISPATCHED, STATUS.CANCELLED],
  [STATUS.DISPATCHED]:   [STATUS.ACKNOWLEDGED, STATUS.CANCELLED],
  [STATUS.ACKNOWLEDGED]: [STATUS.RESOLVED, STATUS.CANCELLED],
  [STATUS.RESOLVED]:     [],
  [STATUS.CANCELLED]:    [],
};

/* Human-facing labels, including the loop stage each maps to, so the UI can
 * show operators where an incident sits in the architecture rather than
 * just a word. */
export const STATUS_META = {
  [STATUS.DETECTED]:     { stage: "SENSE/UNDERSTAND", en: "Detected",     mr: "आढळले",        color: "#0891B2" },
  [STATUS.TRIAGED]:      { stage: "DECIDE",           en: "Triaged",      mr: "प्राधान्यक्रम",  color: "#6366F1" },
  [STATUS.AUTHORISED]:   { stage: "AUTHORIZE",        en: "Authorised",   mr: "मंजूर",         color: "#7C3AED" },
  [STATUS.DISPATCHED]:   { stage: "ACT",              en: "Dispatched",   mr: "रवाना",         color: "#EA580C" },
  [STATUS.ACKNOWLEDGED]: { stage: "ACT",              en: "Acknowledged", mr: "स्वीकारले",     color: "#D97706" },
  [STATUS.RESOLVED]:     { stage: "VERIFY/LEARN",     en: "Resolved",     mr: "निकाली",        color: "#10B981" },
  [STATUS.CANCELLED]:    { stage: "—",                en: "Cancelled",    mr: "रद्द",          color: "#64748B" },
};

export function canTransition(from, to) {
  return (TRANSITIONS[from] || []).includes(to);
}

/* Returns null when the move is legal, or a reason when it is not. Callers
 * surface the reason rather than failing silently — an operator who clicks
 * something that does nothing assumes the system is broken. */
export function transitionError(from, to, { hasApproval } = {}) {
  if (!TRANSITIONS[from]) return `Unknown current status "${from}".`;
  if (from === to) return null;

  if (!canTransition(from, to)) {
    const allowed = TRANSITIONS[from];
    return allowed.length
      ? `Cannot move from "${from}" to "${to}". Allowed: ${allowed.join(", ")}.`
      : `"${from}" is terminal. Reopening requires a new incident that references this one.`;
  }

  /* The one rule the machine will not let the UI talk it out of. */
  if (to === STATUS.DISPATCHED && !hasApproval) {
    return "Dispatch requires a recorded approval. Authorise the decision first.";
  }
  return null;
}

/* ── §22 measurements, derived from timestamps rather than asserted ──────
 * Each returns null when the underlying event has not happened, so a missing
 * measurement reads as missing instead of as zero. */
export function incidentMetrics(incident, events = []) {
  const at = (s) => {
    const e = events.find((x) => x.to_status === s);
    return e ? new Date(e.at).getTime() : null;
  };
  const detected = incident.detected_at ? new Date(incident.detected_at).getTime() : null;
  const authorised = at(STATUS.AUTHORISED);
  const dispatched = at(STATUS.DISPATCHED);
  const acknowledged = at(STATUS.ACKNOWLEDGED);
  const resolved = at(STATUS.RESOLVED);

  const mins = (a, b) => (a && b ? Math.round((b - a) / 60000) : null);

  return {
    decisionSupportMins: mins(detected, authorised),
    dispatchMins: mins(authorised, dispatched),
    acknowledgementMins: mins(dispatched, acknowledged),
    resolutionMins: mins(detected, resolved),
    /* Detection latency needs the time the CONDITION arose, not the time we
       noticed it. No sensor feed means no such timestamp, so this stays null
       rather than being quietly reported as zero. */
    detectionLatencyMins: null,
    detectionLatencyNote:
      "Not measurable without a sensor timestamp for the originating condition.",
  };
}

/* Verification: did the intervention actually improve conditions (§2 VERIFY)?
 * Compares baseline and after observations of the same metric. Returns a
 * verdict only when both exist — an intervention with no measurement is
 * unverified, which is a different thing from ineffective. */
export function verifyOutcome(observations = [], metric = "density") {
  const of = (phase) =>
    observations.filter((o) => o.phase === phase && o.metric === metric);

  const baseline = of("baseline");
  const after = of("after");
  if (!baseline.length || !after.length) {
    return {
      verdict: "unverified",
      reason: !baseline.length
        ? "No baseline measurement was taken before the intervention."
        : "No measurement has been taken since the intervention.",
      delta: null,
    };
  }

  const mean = (rows) => rows.reduce((s, r) => s + Number(r.value), 0) / rows.length;
  const before = mean(baseline);
  const now = mean(after);
  const delta = Number((now - before).toFixed(2));
  const pct = before === 0 ? null : Math.round(((now - before) / before) * 100);

  return {
    verdict: delta < 0 ? "improved" : delta > 0 ? "worsened" : "unchanged",
    before: Number(before.toFixed(2)),
    after: Number(now.toFixed(2)),
    delta,
    pct,
    metric,
    /* Correlation is not causation, and in a live theatre a dozen things
       change at once. The system reports what moved; it does not claim the
       intervention caused it. */
    caveat:
      "Measured change over the intervention window. Other factors change " +
      "simultaneously in a live theatre; this is evidence, not proof of cause.",
  };
}
